import React from 'react';

import { CustomOperatorValue } from '@aline/common';
import { Autocomplete, TextField } from '@mui/material';
import type { GridApi, GridFilterInputValueProps } from '@mui/x-data-grid';
import type { GridColDef, GridFilterItem, GridFilterOperator } from '@mui/x-data-grid-premium';
import { filterAutocompleteStyle } from 'src/components';
import { QuestionOptionsAutocomplete } from 'src/editor';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(() => ({
  autocomplete: filterAutocompleteStyle,
}));

interface ReportFilterValueInputProps {
  applyValue: (item: GridFilterItem) => void;
  column: GridColDef | null;
  item: GridFilterItem;
  booleanLabels?: {
    false: string;
    true: string;
  };
  operator?: GridFilterOperator | null;
  operatorInput?: JSX.Element | null;
  preferScalarInput?: boolean;
}

/**
 * Controlled report-filter value renderer. It has no DataGrid context and
 * receives any operator-owned input and localized labels from its adapter.
 */
export const ReportFilterValueInput = React.memo(function ReportFilterValueInput({
  applyValue,
  booleanLabels = { false: 'False', true: 'True' },
  column,
  item,
  operator,
  operatorInput,
  preferScalarInput = false,
}: ReportFilterValueInputProps): JSX.Element | null {
  const { classes } = useStyles();

  if (!operator || !item.field || !column) return null;

  if (
    preferScalarInput &&
    (column.type === 'string' || column.type === 'number') &&
    !getIsCustomOperator(operator.value)
  )
    return (
      <TextField
        defaultValue={getScalarInputValue(item.value)}
        type={column.type === 'number' ? 'number' : 'text'}
        variant="standard"
        onChange={event => applyValue({ ...item, value: event.target.value })}
      />
    );

  if (operatorInput) return operatorInput;

  switch (column.type) {
    case 'boolean': {
      const booleanOptions = [booleanLabels.true, booleanLabels.false];
      const currentValue = booleanOptions.find(opt => opt === item.value) ?? null;

      return (
        <Autocomplete
          options={booleanOptions}
          renderInput={params => <TextField {...params} variant="standard" />}
          value={currentValue}
          onChange={(_event, newValue) => {
            applyValue({ ...item, value: newValue ?? undefined });
          }}
        />
      );
    }
    case 'singleSelect': {
      return (
        <QuestionOptionsAutocomplete
          className={classes.autocomplete}
          multiple={
            operator.value === 'isAnyOf' ||
            operator.value === CustomOperatorValue.QuestionSelectNotAnyOf
          }
          questionId={item.field}
          value={item.value}
          onChange={options => applyValue({ ...item, value: options.map(o => o.label) })}
        />
      );
    }
    default:
      return null;
  }
});

interface ReportFilterValueInputAdapterProps extends Pick<
  ReportFilterValueInputProps,
  'applyValue' | 'column' | 'item' | 'operator'
> {
  apiRef?: React.RefObject<GridApi | null>;
}

/**
 * DataGrid boundary for the pure value renderer. Production passes `apiRef`;
 * the approval card omits it and uses the same controlled component.
 */
export function ReportFilterValueInputAdapter({
  apiRef,
  applyValue,
  column,
  item,
  operator,
}: ReportFilterValueInputAdapterProps): JSX.Element | null {
  if (!operator || !item.field || !column) return null;

  if (apiRef?.current)
    return (
      <ReportFilterValueInput
        applyValue={applyValue}
        booleanLabels={{
          false: apiRef.current.getLocaleText('filterValueFalse'),
          true: apiRef.current.getLocaleText('filterValueTrue'),
        }}
        column={column}
        item={item}
        operator={operator}
        operatorInput={renderGridOperatorInput({ apiRef, applyValue, item, operator })}
      />
    );

  return (
    <ReportFilterValueInput
      preferScalarInput
      applyValue={applyValue}
      column={column}
      item={item}
      operator={operator}
      operatorInput={renderStandaloneOperatorInput({ applyValue, item, operator })}
    />
  );
}

function renderGridOperatorInput({
  apiRef,
  applyValue,
  item,
  operator,
}: {
  apiRef: React.RefObject<GridApi | null>;
  applyValue: (item: GridFilterItem) => void;
  item: GridFilterItem;
  operator: GridFilterOperator;
}): JSX.Element | null {
  if (!operator.InputComponent) return null;
  const props = getOperatorInputProps(operator);
  return (
    // @ts-expect-error MUI v9: useGridApiRef returns RefObject<X | null> but operator.InputComponent requires RefObject<X>. The adapter only calls this after guarding apiRef.current.
    <operator.InputComponent apiRef={apiRef} applyValue={applyValue} item={item} {...props} />
  );
}

function renderStandaloneOperatorInput({
  applyValue,
  item,
  operator,
}: {
  applyValue: (item: GridFilterItem) => void;
  item: GridFilterItem;
  operator: GridFilterOperator;
}): JSX.Element | null {
  if (!operator.InputComponent) return null;
  const props = getOperatorInputProps(operator);
  return (
    // @ts-expect-error MUI types require apiRef, but report custom inputs use only item and applyValue in the standalone panel.
    <operator.InputComponent applyValue={applyValue} item={item} {...props} />
  );
}

function getOperatorInputProps(
  operator: GridFilterOperator
): Partial<GridFilterInputValueProps> & { variant: 'standard' } {
  return {
    ...operator.InputComponentProps,
    slotProps: {
      root: {
        label: null,
      },
    },
    variant: 'standard',
  };
}

function getScalarInputValue(value: unknown): string | number {
  return typeof value === 'string' || typeof value === 'number' ? value : '';
}

function getIsCustomOperator(value: GridFilterOperator['value']): boolean {
  if (typeof value !== 'string') return false;
  return Object.values<string>(CustomOperatorValue).some(custom => custom === value);
}
